import { ChangeEvent, CSSProperties } from 'react';
import cns from 'classnames';
import { useTranslation } from 'next-i18next';
import { useDialog } from '@zep/dialog';
import { PlusIcon, TrashIcon } from '@zep/icons';
import { StoreImageType } from '../../../../consts';
import { useUploadImages } from '../../../../services';
import S from './UploadThumbnailCard.module.scss';

/**
 * 에셋 업로드시 썸네일의 등록/삭제를 관리하는 컴포넌트
 */
const UploadThumbnailCard = (props: StoreThumbnailCardProps) => {
  const {
    thumbnail,
    onThumbnailUpload,
    disabled,
    isAppThumbnail,
    thumbnailKey = 'image',
    aspectRatio = '1/1',
    maxWidth = 150
  } = props;
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['store']);
  const imageUploadMutation = useUploadImages(StoreImageType.thumbnail, {
    onSuccess: data => {
      if (data.status === 0) {
        onThumbnailUpload(data.keys[0]);
      }
    },
    onError: error => {
      console.error(error);
      dialog.alert({
        title: error.message
      });
    }
  });

  /**
   * 썸네일 추가시 이미지 파일을 후처리하는 함수
   * @param event 체인지 이벤트 파라미터
   */
  const handleThumbnailUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) {
      return;
    }
    const file = event.target.files[0];
    if (file.type !== 'image/png' && file.type !== 'image/jpeg' && file.type !== 'image/webp') {
      dialog.alert({
        title: t('store:store_alert_wrong_image_desc')
      });
      event.target.value = '';
      return;
    } else if (file.size > 1024 * 400) {
      dialog.alert({
        title: t('store:store_image_size_limit', {
          size: '400KB'
        })
      });
      event.target.value = '';
      return;
    } else if (file.name.length > 100) {
      dialog.alert({
        title: t('store:image_max_name_length')
      });
      event.target.value = '';
      return;
    }
    imageUploadMutation.mutate(event.target.files, {
      onSettled: () => {
        event.target.value = '';
      }
    });
  };
  return thumbnail ? <div className={cns(S.thumbnail_wrapper, {
    [S.app_thumbnail]: isAppThumbnail
  })} style={{
    '--thumbnail-max-width': maxWidth,
    '--thumbnail-aspeat-ratio': aspectRatio
  } as CSSProperties} data-sentry-component="UploadThumbnailCard" data-sentry-source-file="UploadThumbnailCard.tsx">
      <div className={S.thumbnail_image}>
        <img src={`${process.env.NEXT_PUBLIC_STORAGE_URL}/${thumbnail}`} alt="thumbnail" />
      </div>
      {!disabled && <button type="button" className={S.thumbnail_delete_button} onClick={() => !disabled && onThumbnailUpload('')}>
          <TrashIcon width={16} height={16} aria-label="Delete" />
        </button>}
    </div> : <div className={S.empty_thumbnail} style={{
    '--thumbnail-max-width': maxWidth,
    '--thumbnail-aspeat-ratio': aspectRatio
  } as CSSProperties} data-sentry-component="UploadThumbnailCard" data-sentry-source-file="UploadThumbnailCard.tsx">
      <input type="file" name="thumbnail" id={`thumbnail-${thumbnailKey}-input`} onChange={handleThumbnailUpload} accept=".jpg, .jpeg, .png, .webp" disabled={disabled} />
      <label className={cns(S.thumbnail_input, {
      [S.app_thumbnail]: isAppThumbnail
    })} htmlFor={`thumbnail-${thumbnailKey}-input`}>
        <PlusIcon width={50} height={50} aria-label="Plus" data-sentry-element="PlusIcon" data-sentry-source-file="UploadThumbnailCard.tsx" />
        <div className={S.add_text}>{t('store:store_add_image')}</div>
      </label>
    </div>;
};
export default UploadThumbnailCard;
type StoreThumbnailCardProps = {
  thumbnail?: string | ArrayBuffer;
  thumbnailKey?: string;
  onThumbnailUpload: (newThumbnail: string) => void;
  disabled?: boolean;
  isAppThumbnail?: boolean;
  maxWidth?: number;
  aspectRatio?: string;
};